import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import styles from './UserAttendance.module.scss';
import { Dropdown, filterBarSelectStyles } from '@/components/Dropdown';
import BlockUi from 'react-block-ui';
import { useDispatch, useSelector } from 'react-redux';
import { memberActionTypes } from '@store/Member/Member.actionTypes';
import firebase from '@config/firebase.config';
import { leaveBalanceService } from '@/services/leaveBalance';
import moment from 'moment-timezone';
import {
  userLeaveBalanceConfig,
  userLeaveColorConfig,
  userLeaveEnum,
  userLeaveLabelEnum,
} from './UserAttendance.constants';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import {
  FiClock,
  FiCalendar,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';

import { useTeamMemberDetailsForSelect } from '@/hooks/useTeamMemberDetailsForSelect';
import { Button } from 'reactstrap';
import { Button as AppButton, buttonVariants } from '@/components/Button';
import { StatCard } from '@/components/StatsCards';
import { DonutBreakdown } from '@/components/DonutBreakdown';
import { MetricTile } from '@/components/MetricTile';
import { RecentActivityList } from '@/components/RecentActivityList';
import { MonthCalendarGrid } from '@/components/MonthCalendarGrid';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

import {
  useUrlDateRangeSync,
  useUrlFilterWrite,
} from '@/hooks/useUrlFilterSync';
import { getReportFiltersFromSearch } from '@/utils/reportFiltersUrl';
import { CiExport } from 'react-icons/ci';
import httpClient from '@/config/httpClient';
import { addNotification, notificationStates } from '@/components/Notification';
import { useWorkingSchedule } from '@/hooks/useWorkingSchedule';
import { useEffectiveHolidays } from '@/hooks/useEffectiveHolidays';
import { isWorkingDay, getHolidayForDate } from '@/utils/attendanceUtils';

const isFutureDate = (date) => date.get() > new Date().getTime();

// Shared by the current-month calendar fetch and the previous-month trend
// fetch so the present/absent/leave classification rules never drift apart.
const classifyDayStatus = ({
  momentDate,
  presentFlag,
  leaveType,
  workingDays,
  holidays,
}) => {
  let Status, color, key;
  const holiday = getHolidayForDate(momentDate, holidays);
  if (leaveType) {
    Status = leaveType; // show leave type e.g. "Paid Leave", "Sick Leave", "Casual Leave"
    if (leaveType === userLeaveLabelEnum.sickLeave) {
      color = userLeaveColorConfig[userLeaveEnum.sickLeave];
      key = userLeaveEnum.sickLeave;
    } else if (leaveType === userLeaveLabelEnum.casualLeave) {
      color = userLeaveColorConfig[userLeaveEnum.casualLeave];
      key = userLeaveEnum.casualLeave;
    } else if (leaveType === userLeaveLabelEnum.plannedLeave) {
      color = userLeaveColorConfig[userLeaveEnum.plannedLeave];
      key = userLeaveEnum.plannedLeave;
    } else if (leaveType === userLeaveLabelEnum.unpaidLeave) {
      color = userLeaveColorConfig[userLeaveEnum.unpaidLeave];
      key = userLeaveEnum.unpaidLeave;
    } else {
      color = userLeaveColorConfig[userLeaveEnum.absent];
      key = userLeaveEnum.absent;
    }
  } else if (presentFlag) {
    Status = userLeaveLabelEnum.present;
    color = userLeaveColorConfig[userLeaveEnum.present];
    key = userLeaveEnum.present;
  } else if (holiday) {
    Status = holiday.name;
    color = userLeaveColorConfig[userLeaveEnum.holiday];
    key = userLeaveEnum.holiday;
  } else if (!isWorkingDay(momentDate, workingDays)) {
    Status = userLeaveLabelEnum.weekOff;
    color = userLeaveColorConfig[userLeaveEnum.weekOff];
    key = userLeaveEnum.weekOff;
  } else if (isFutureDate(momentDate)) {
    Status = ' ';
    color = userLeaveColorConfig[userLeaveEnum.absent];
    key = userLeaveEnum.absent;
  } else {
    Status = userLeaveLabelEnum.absent;
    color = userLeaveColorConfig[userLeaveEnum.absent];
    key = userLeaveEnum.absent;
  }
  return { Status, color, key };
};

const getPreviousMonthYear = (month, year) =>
  month === 1
    ? { prevMonth: 12, prevYear: year - 1 }
    : { prevMonth: month - 1, prevYear: year };

const emptyLeaveCounts = () => ({
  present: 0,
  absent: 0,
  weekOff: 0,
  holiday: 0,
  sickLeave: 0,
  casualLeave: 0,
  plannedLeave: 0,
  unpaidLeave: 0,
});

export const UserAttendanceComponent = () => {
  const database = firebase.firestore();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => ({
    userProfile: state.auth?.profile,
    activeCompany: state.auth?.profile?.activeCompany,
    companyList: state.auth?.profile?.companyList,
    activeTeamId: state.team.selectedTeamID,
    activeUserId: state.team.activeUserID,
    all: state.team.all,
  }));
  const activeTeamId = user.activeTeamId ?? null;
  const { workingSchedule } = useWorkingSchedule();
  const companyWorkingDays = workingSchedule.days;
  const { holidays: effectiveHolidays } = useEffectiveHolidays(activeTeamId);
  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useTeamMemberDetailsForSelect(activeTeamId);

  const options = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .map(([uid, m]) => ({
        value: uid,
        label: m?.userName || m?.userEmailid || uid,
      }))
      .filter((o) => o.value)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );

  const exportTypeRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectOption, setSelectOptions] = useState({});
  const [statess, setStatess] = useState({
    open: '',
    selectedTitle: '',
  });
  const [timeLogInfo, setTimeLogInfo] = useState([]);
  const [previousMonthCounts, setPreviousMonthCounts] = useState(null);
  // Raw per-day data fetched from Firestore, classified into timeLogInfo /
  // previousMonthCounts by the effects below. Kept separate from that
  // classification so it re-runs whenever companyWorkingDays/effectiveHolidays
  // finish loading, rather than baking in whatever was available (often
  // still the empty default) at the moment the fetch happened to complete.
  const [rawMonthData, setRawMonthData] = useState(null);
  const [rawPrevMonthData, setRawPrevMonthData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCount, setShowCount] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const { start } = getReportFiltersFromSearch(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    return start ? moment(start, 'YYYY-MM-DD').toDate() : new Date();
  });
  const isCalenderMonthCalledRef = useRef(false);

  const dateRange = useMemo(() => {
    const m = moment(currentMonth);
    return {
      start: m.clone().startOf('month').format('YYYY-MM-DD'),
      end: m.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }, [currentMonth]);
  const setDateRangeForPop = useCallback((updater) => {
    setCurrentMonth((prev) => {
      const curStart = moment(prev).startOf('month').format('YYYY-MM-DD');
      const curEnd = moment(prev).endOf('month').format('YYYY-MM-DD');
      const cur = { start: curStart, end: curEnd };
      const next = typeof updater === 'function' ? updater(cur) : updater;
      if (next?.start) return moment(next.start).toDate();
      return prev;
    });
  }, []);

  useUrlDateRangeSync({ location, setDateRange: setDateRangeForPop });

  const urlWriteParams = useMemo(() => {
    const params = { start: dateRange.start, end: dateRange.end };
    if (selectOption?.value) params.member = selectOption.value;
    params.tz = moment.tz.guess();
    return params;
  }, [dateRange.start, dateRange.end, selectOption?.value]);

  useUrlFilterWrite({
    history,
    location,
    params: urlWriteParams,
    enabled: Boolean(activeTeamId && dateRange?.start && dateRange?.end),
  });
  const timeLogByDate = useMemo(() => {
    const map = new Map();
    timeLogInfo.forEach((item) => {
      map.set(item.date, item);
    });
    return map;
  }, [timeLogInfo]);
  const getDayContent = useCallback(
    (dateStr) => {
      const log = timeLogByDate.get(dateStr);
      if (!log) return null;
      return (
        <div className={styles.dateCellWrapper}>
          <div className={styles.statusPill} style={{ background: log.color }}>
            <span className={styles.statusDot} />
            <Text
              variant={textVariantsEnum.Text_xs}
              fontWeight={fontWeightEnum.Normal}
            >
              {log.Status}
            </Text>
          </div>
          {log.key === userLeaveEnum.present && (
            <div className={styles.durationRow}>
              <FiClock size={11} />
              {/* Placeholder — wire to real per-day check-in/out once this view fetches it */}
              <Text variant={textVariantsEnum.Text_xxs} color="gray-500">
                8h 30m
              </Text>
            </div>
          )}
        </div>
      );
    },
    [timeLogByDate]
  );

  const getMembersTimeInfo = async (month, year, userId) => {
    try {
      setIsLoading(true);
      const newDate = new Date(year, month, 0);
      const monthString = month.toString().padStart(2, '0');
      const daysInMonth = newDate.getDate();
      const formattedDays = [];
      for (let day = 1; day <= daysInMonth; day++) {
        formattedDays.push(
          `${day.toString().padStart(2, '0')}:${monthString}:${year}`
        );
      }
      const formattedMonth = `${newDate
        .toLocaleString('default', { month: 'long' })
        .toLowerCase()}-${year}`;

      const entireMonthDocData = await database
        .collection('membersDatabase')
        .doc(userId)
        .collection('attendanceByMonth')
        .doc(formattedMonth)
        .get();

      const entireMonthData = entireMonthDocData.data() || {};

      // Approved leave from company (single source)
      let approvedLeaveByDate = {};
      if (user?.activeCompany?.companyId) {
        try {
          const startDate = `01-${monthString}-${year}`;
          const endDate = `${String(daysInMonth).padStart(
            2,
            '0'
          )}-${monthString}-${year}`;
          const approvedLeave =
            await leaveBalanceService.getApprovedLeaveForUserInDateRange(
              user.activeCompany.companyId,
              userId,
              startDate,
              endDate
            );
          approvedLeave.forEach(({ leaveDate, type }) => {
            approvedLeaveByDate[leaveDate] = type;
          });
        } catch (e) {
          // ignore
        }
      }
      // Classification (below, in a separate effect) needs
      // companyWorkingDays/effectiveHolidays, which can still be loading at
      // this point - store the raw data and let that effect classify it
      // once those are ready, instead of classifying here with whatever
      // they happen to be right now.
      setRawMonthData({ formattedDays, entireMonthData, approvedLeaveByDate });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Classifies the raw fetch above into timeLogInfo - re-runs whenever the
  // raw data changes OR companyWorkingDays/effectiveHolidays finish loading
  // after the fetch already completed.
  useEffect(() => {
    if (!rawMonthData) return;
    const { formattedDays, entireMonthData, approvedLeaveByDate } =
      rawMonthData;
    let newTimeLogInfo = [];
    for (let i = 0; i < formattedDays.length; i++) {
      const day = formattedDays[i];
      const data = entireMonthData[day];
      const momentDate = moment(day, 'DD:MM:YYYY');
      const date = momentDate.format('YYYY-MM-DD');
      const leaveType = approvedLeaveByDate[day];
      const { Status, color, key } = classifyDayStatus({
        momentDate,
        presentFlag: data?.present === true,
        leaveType,
        workingDays: companyWorkingDays,
        holidays: effectiveHolidays,
      });
      newTimeLogInfo.push({ Status, date, color, key });
    }
    newTimeLogInfo = newTimeLogInfo.filter((item) => item.Status !== ' ');
    setTimeLogInfo(newTimeLogInfo);
  }, [rawMonthData, companyWorkingDays, effectiveHolidays]);

  // Lightweight counterpart to getMembersTimeInfo used only to power the
  // stat cards' "vs last month" trend — same classification, counts only.
  const getPreviousMonthCounts = async (month, year, userId) => {
    try {
      const newDate = new Date(year, month, 0);
      const monthString = month.toString().padStart(2, '0');
      const daysInMonth = newDate.getDate();
      const formattedMonth = `${newDate
        .toLocaleString('default', { month: 'long' })
        .toLowerCase()}-${year}`;

      const entireMonthDocData = await database
        .collection('membersDatabase')
        .doc(userId)
        .collection('attendanceByMonth')
        .doc(formattedMonth)
        .get();
      const entireMonthData = entireMonthDocData.data() || {};

      let approvedLeaveByDate = {};
      if (user?.activeCompany?.companyId) {
        try {
          const startDate = `01-${monthString}-${year}`;
          const endDate = `${String(daysInMonth).padStart(
            2,
            '0'
          )}-${monthString}-${year}`;
          const approvedLeave =
            await leaveBalanceService.getApprovedLeaveForUserInDateRange(
              user.activeCompany.companyId,
              userId,
              startDate,
              endDate
            );
          approvedLeave.forEach(({ leaveDate, type }) => {
            approvedLeaveByDate[leaveDate] = type;
          });
        } catch (e) {
          // ignore
        }
      }

      // Classification (below, in a separate effect) needs
      // companyWorkingDays/effectiveHolidays, which can still be loading at
      // this point - store the raw data and let that effect classify it
      // once those are ready, instead of classifying here with whatever
      // they happen to be right now.
      setRawPrevMonthData({
        daysInMonth,
        monthString,
        year,
        entireMonthData,
        approvedLeaveByDate,
      });
    } catch (error) {
      console.error(error);
    }
  };

  // Classifies the raw fetch above into previousMonthCounts - re-runs
  // whenever the raw data changes OR companyWorkingDays/effectiveHolidays
  // finish loading after the fetch already completed.
  useEffect(() => {
    if (!rawPrevMonthData) return;
    const {
      daysInMonth,
      monthString,
      year,
      entireMonthData,
      approvedLeaveByDate,
    } = rawPrevMonthData;
    const counts = emptyLeaveCounts();
    for (let day = 1; day <= daysInMonth; day++) {
      const formattedDay = `${day
        .toString()
        .padStart(2, '0')}:${monthString}:${year}`;
      const data = entireMonthData[formattedDay];
      const momentDate = moment(formattedDay, 'DD:MM:YYYY');
      const leaveType = approvedLeaveByDate[formattedDay];
      const { Status, key } = classifyDayStatus({
        momentDate,
        presentFlag: data?.present === true,
        leaveType,
        workingDays: companyWorkingDays,
        holidays: effectiveHolidays,
      });
      if (Status !== ' ' && counts[key] !== undefined) {
        counts[key]++;
      }
    }
    setPreviousMonthCounts(counts);
  }, [rawPrevMonthData, companyWorkingDays, effectiveHolidays]);

  function getCalenderMonth(date, userId) {
    if (user?.activeTeamId && !isCalenderMonthCalledRef.current) {
      setTimeLogInfo([]);
      setRawMonthData(null);
      const month = date?.getMonth() + 1;
      const year = date?.getFullYear();
      getMembersTimeInfo(month, year, userId);
      const { prevMonth, prevYear } = getPreviousMonthYear(month, year);
      getPreviousMonthCounts(prevMonth, prevYear, userId);
      isCalenderMonthCalledRef.current = true;
    }
  }

  const [dataFetched, setDataFetched] = useState(false);
  const [selectedUser, setSelectedUser] = useState(user);
  const handleSelectChange = (option) => {
    setSelectedUser(option);
    dispatch({
      type: memberActionTypes.GET_USER_TIMELOG_DATA,
      payload: {
        activeUserName: option?.label,
      },
    });
    setShowCount(true);
    setSelectOptions(option || {});
    setTimeLogInfo([]); // Reset the timeLogInfo state
    setRawMonthData(null);
    if (option && option.value) {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      if (!dataFetched) {
        getMembersTimeInfo(month, year, option.value);
        const { prevMonth, prevYear } = getPreviousMonthYear(month, year);
        getPreviousMonthCounts(prevMonth, prevYear, option.value);
        isCalenderMonthCalledRef.current = true;
      } else {
        setDataFetched(false); // Reset the dataFetched flag
        isCalenderMonthCalledRef.current = false;
      }
    }
  };

  // Set default member when options are ready; `member` query wins, then profile, then first.
  useEffect(() => {
    if (!activeTeamId) {
      setSelectOptions({});
      setSelectedUser({
        value: user.userProfile?.id,
        label: user.userProfile?.name,
      });
      return;
    }
    if (options.length === 0) return;
    if (
      selectOption?.value &&
      options.some((o) => o.value === selectOption.value)
    ) {
      return;
    }
    if (filtersFromUrl.member) {
      const fromUrl = options.find((o) => o.value === filtersFromUrl.member);
      if (fromUrl) {
        handleSelectChange(fromUrl);
        return;
      }
    }
    const defaultOption =
      options.find((o) => o.value === user.userProfile?.id) || options[0];
    if (defaultOption) {
      handleSelectChange(defaultOption);
    }
  }, [
    activeTeamId,
    options,
    user.userProfile?.id,
    user.userProfile?.name,
    selectOption?.value,
    filtersFromUrl.member,
  ]);

  const [leaveBalanceConfig, setLeaveBalanceConfig] =
    useState(emptyLeaveCounts());

  const calculatePresentAndLeave = () => {
    const leaveBalanceConfig = emptyLeaveCounts();
    timeLogInfo.forEach((item) => {
      leaveBalanceConfig[item.key]++;
    });

    setLeaveBalanceConfig(leaveBalanceConfig);
  };

  useMemo(() => {
    calculatePresentAndLeave();
  }, [timeLogInfo]);

  // "vs last month" trend per leave-type stat card — omitted (not just 0%)
  // when there's no previous-month data to compare against.
  const prevMonthShortLabel = useMemo(
    () => moment(currentMonth).subtract(1, 'month').format('MMM'),
    [currentMonth]
  );
  const trendByKey = useMemo(() => {
    if (!previousMonthCounts) return {};
    const result = {};
    userLeaveBalanceConfig.forEach(({ key }) => {
      const prev = previousMonthCounts[key] || 0;
      if (prev === 0) {
        result[key] = null;
        return;
      }
      const current = leaveBalanceConfig[key] || 0;
      const pct = Math.round(((current - prev) / prev) * 100);
      result[key] = { pct: Math.abs(pct), direction: pct >= 0 ? 'up' : 'down' };
    });
    return result;
  }, [previousMonthCounts, leaveBalanceConfig]);

  const totalDaysTracked = timeLogInfo.length;
  const daysInSelectedMonth = useMemo(
    () => moment(currentMonth).daysInMonth(),
    [currentMonth]
  );
  const workingDays = Math.max(
    daysInSelectedMonth - (leaveBalanceConfig.weekOff || 0),
    0
  );
  const attendancePct =
    workingDays > 0
      ? Number(((leaveBalanceConfig.present / workingDays) * 100).toFixed(1))
      : 0;

  const donutSegments = useMemo(
    () =>
      userLeaveBalanceConfig.map((item) => {
        const value = leaveBalanceConfig[item.key] || 0;
        return {
          key: item.key,
          label: item.label,
          value,
          percentage:
            totalDaysTracked > 0
              ? Math.round((value / totalDaysTracked) * 100)
              : 0,
          color: item.color,
        };
      }),
    [leaveBalanceConfig, totalDaysTracked]
  );

  const recentActivityItems = useMemo(() => {
    const today = moment();
    const yesterday = moment().subtract(1, 'day');
    // Punch time isn't fetched by this view yet — placeholder rotation until
    // real check-in data is wired in.
    const placeholderTimes = [
      '9:15 AM',
      '9:10 AM',
      '9:05 AM',
      '9:00 AM',
      '8:55 AM',
    ];
    return [...timeLogInfo]
      .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
      .slice(0, 5)
      .map((item, index) => {
        const dateMoment = moment(item.date);
        let dateLabel;
        if (dateMoment.isSame(today, 'day')) dateLabel = 'Today';
        else if (dateMoment.isSame(yesterday, 'day')) dateLabel = 'Yesterday';
        else dateLabel = dateMoment.format('DD MMM YYYY');
        return {
          id: item.date,
          statusLabel: item.Status,
          statusColor: item.color,
          metaLabel: dateMoment.format('DD MMM YYYY'),
          dateLabel,
          timeLabel:
            placeholderTimes[index] ||
            placeholderTimes[placeholderTimes.length - 1],
        };
      });
  }, [timeLogInfo]);

  let activeUser = {
    activeTeamId: user.activeTeamId,
    activeUserId: selectedUser.value ? selectedUser.value : user.userProfile.id,
  };
  const applySelectedMonth = (date) => {
    isCalenderMonthCalledRef.current = false;
    setCurrentMonth(date);
    getCalenderMonth(date, selectOption?.value);
  };
  if (activeTeamId && membersLoading && options.length === 0) {
    return (
      <div className="loader-container">
        <BlockUi tag="div" blocking message="Loading, please wait" />
      </div>
    );
  }
  const handleExport = async ({ exportType }) => {
    setExportLoading(true);
    try {
      const requestPayload = {
        exportType,
        loggedInUser: {
          id: user.userProfile?.id,
          email: user.userProfile?.email,
          name: user.userProfile?.name,
        },
        memberName: selectOption?.label,
        teamId: activeTeamId,
        memberId: selectOption?.value,
        companyId: user.activeCompany?.companyId,
        companyName: user.activeCompany?.companyName,
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear(),
      };

      await httpClient.post('/exportUserAttendanceCsv', {
        ...requestPayload,
      });
    } catch (error) {
      addNotification({
        message: 'Failed to export user attendance',
        level: notificationStates.error,
        autoDismiss: 4,
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className={styles.userAttendancePage}>
      {membersError && activeTeamId && (
        <div className="mb-1 text-danger small">
          Failed to load members. {membersErrorObj?.message || ''}{' '}
          <Button
            color="link"
            size="sm"
            className="p-0"
            onClick={() => refetchMembers()}
          >
            Retry
          </Button>
        </div>
      )}
      <div className={styles.filtersContainer}>
        <div className={styles.dropdown}>
          <Dropdown
            options={options}
            value={selectOption}
            placeholder="Select member..."
            onChange={handleSelectChange}
            isLoading={membersLoading}
            isDisabled={!activeTeamId || membersError}
            selectStyles={filterBarSelectStyles}
          />
        </div>

        <div className={styles.monthField}>
          <DatePicker
            className={styles.monthYearPicker}
            popupClassName={styles.monthPopup}
            placement="bottomLeft"
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            picker="month"
            allowClear={false}
            suffixIcon={null}
            prefix={<FiCalendar size={16} className={styles.monthFieldIcon} />}
            format="MMMM YYYY"
            value={dayjs(currentMonth)}
            onChange={(d) => {
              if (!d) return;
              applySelectedMonth(d.startOf('month').toDate());
            }}
          />
          <div className={styles.monthArrows}>
            <button
              type="button"
              className={styles.monthArrowButton}
              aria-label="Previous month"
              onClick={() => {
                applySelectedMonth(
                  moment(currentMonth).subtract(1, 'month').toDate()
                );
              }}
            >
              <FiChevronLeft size={18} />
            </button>
            <button
              type="button"
              className={styles.monthArrowButton}
              aria-label="Next month"
              onClick={() => {
                applySelectedMonth(
                  moment(currentMonth).add(1, 'month').toDate()
                );
              }}
            >
              <FiChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className={styles.exportButtonContainer}>
          <AppButton
            variant={buttonVariants.outline}
            text="Export"
            icon={<CiExport size={16} />}
            loading={exportLoading && exportTypeRef.current === 'member'}
            disabled={exportLoading && exportTypeRef.current === 'team'}
            onClick={() => {
              setExportLoading(true);
              exportTypeRef.current = 'member';
              handleExport({ exportType: 'member' });
            }}
          />
          {/* <AppButton
            variant={buttonVariants.primary}
            text="Export Department Attendance"
            icon={<CiExport size={16} />}
            loading={exportLoading && exportTypeRef.current === 'team'}
            disabled={exportLoading && exportTypeRef.current === 'member'}
            onClick={() => {
              setExportLoading(true);
              exportTypeRef.current = 'team';
              handleExport({ exportType: 'team' });
            }}
          /> */}
        </div>
      </div>
      {membersError && (
        <div className={styles.memberError}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            Failed to load members. {membersErrorObj?.message || ''}
          </Text>
          <Button color="link" size="sm" onClick={() => refetchMembers()}>
            Retry
          </Button>
        </div>
      )}

      <div className={styles.statsRow}>
        {userLeaveBalanceConfig.map((item) => {
          const Icon = item.icon;
          const trend = trendByKey[item.key];
          return (
            <StatCard
              key={item.key}
              title={item.label.toUpperCase()}
              value={leaveBalanceConfig[item.key]}
              icon={<Icon size={14} color={`var(--${item.iconColor})`} />}
              iconBg={item.iconBg}
              contaninerClassName={styles.statCardTile}
              trend={
                trend
                  ? {
                      text: `${trend.direction === 'up' ? '↑' : '↓'} ${
                        trend.pct
                      }% vs ${prevMonthShortLabel}`,
                    }
                  : undefined
              }
              compact
            />
          );
        })}
      </div>

      <div className={styles.pageBody}>
        <div className={styles.calendarContainer}>
          {isLoading ? (
            <div className="loader-container">
              <BlockUi tag="div" blocking message="Loading, please wait" />
            </div>
          ) : (
            <MonthCalendarGrid
              monthDate={currentMonth}
              getDayContent={getDayContent}
            />
          )}
          <div className={styles.calendarLegend}>
            {userLeaveBalanceConfig.map((item) => (
              <div key={item.key} className={styles.legendChip}>
                <span
                  className={styles.legendDot}
                  style={{ background: item.color }}
                />
                <Text variant={textVariantsEnum.Text_xs} color="gray-600">
                  {item.label}
                </Text>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.sidebarColumn}>
          <div className={styles.sidebarCard}>
            <DonutBreakdown
              title="Attendance Overview"
              centerValue={totalDaysTracked}
              centerLabel="Total Days"
              segments={donutSegments}
            />
          </div>

          <div className={styles.sidebarCard}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
            >
              Working Summary
            </Text>
            <div className={styles.workingSummaryRow}>
              <MetricTile
                value={workingDays}
                label="Working Days"
                icon={<FiCalendar size={16} color="var(--purple-600)" />}
              />
              <MetricTile
                value={`${attendancePct}%`}
                label="Attendance %"
                icon={<FiTrendingUp size={16} color="var(--purple-600)" />}
              />
            </div>
          </div>

          {/* <div
            className={classNames(styles.sidebarCard, styles.sidebarCard_grow)}
          >
            <RecentActivityList
              title="Recent Activity"
              items={recentActivityItems}
            />
          </div> */}
        </div>
      </div>
    </div>
  );
};
